import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';

import type { Rule } from 'antd-v5/es/form';
import type { DateTime } from 'luxon';

import type {
  RequirementFormValues,
  RequirementNode,
  RequirementRowPolicy,
} from './types';

/**
 * The per-list configuration every row needs and none of them vary.
 *
 * Deliberately small: only values identical for *every* requirement belong
 * here, per-row data stays a prop
 */
export interface RequirementsContextValue {
  resolvePolicy: (requirement: RequirementNode) => RequirementRowPolicy;
  /** The row currently saving; only that row shows a spinner */
  savingRequirementId?: number | null;
  /** Temporarily uneditable, e.g. while a version switch is in flight */
  disabled?: boolean;
  overrideContent?: (id: number, requirement: RequirementNode) => ReactNode;
  extraContent?: (id: number, requirement: RequirementNode) => ReactNode;
  /** Overrides the built-in "not in the future" bound on the completion date */
  disabledDate?: (date: DateTime, requirement: RequirementNode) => boolean;
  /** Appended after the built-in no-future rule */
  completionDateRules?: Rule[];
  /** Rejecting marks the save as failed; see `RequirementsRendererProps` */
  onSaveRequirement?: (
    id: number,
    values: RequirementFormValues,
    root: RequirementNode,
  ) => void | Promise<unknown>;
}

const RequirementsContext = createContext<RequirementsContextValue | null>(
  null,
);

export const RequirementsProvider = RequirementsContext.Provider;

export function useRequirementsContext(): RequirementsContextValue {
  const value = useContext(RequirementsContext);

  if (!value) {
    throw new Error(
      'Requirement rows must be rendered inside <RequirementsRenderer />',
    );
  }

  return value;
}
